import { Plus, Upload } from "lucide-react"
import { useCallback } from "react"
import { useDropzone } from "react-dropzone"

import { cn } from "@/lib/utils"

interface FileDropzoneProps {
  onFilesAdded: (files: File[]) => void
  accept?: Record<string, string[]>
  multiple?: boolean
  className?: string
  label?: string
  compact?: boolean
}

export function FileDropzone({
  onFilesAdded,
  accept = { "application/pdf": [".pdf"] },
  multiple = true,
  className,
  label,
  compact = false,
}: FileDropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFilesAdded(acceptedFiles)
    },
    [onFilesAdded],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
  })

  if (compact) {
    return (
      <div
        {...getRootProps()}
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 transition-colors",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50",
          className,
        )}
      >
        <input {...getInputProps()} />
        <Plus className="text-muted-foreground h-5 w-5" />
        <span className="text-muted-foreground text-sm">
          {isDragActive ? "Drop files here" : "Add more files"}
        </span>
      </div>
    )
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors",
        isDragActive
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/25 hover:border-primary/50",
        className,
      )}
    >
      <input {...getInputProps()} />
      <Upload className="text-muted-foreground h-10 w-10" />
      <p className="mt-4 text-sm font-medium">
        {isDragActive
          ? "Drop files here"
          : (label ?? "Drag & drop PDF files here")}
      </p>
      <p className="text-muted-foreground mt-1 text-xs">or click to browse</p>
    </div>
  )
}
